import { ref, shallowRef, type Ref, type ShallowRef } from 'vue';
import { logger } from '@/utils/logger';

/** Which strip a row is showing in place of itself, if any. */
export type RowDeleteState = 'confirm' | 'busy' | 'error' | null;

export interface RowDeleteError {
  uid: string;
  text: string;
}

/** The minimum a row must carry: the id the three states key off. */
export interface RowDeleteTarget {
  uid: string;
}

export interface RowDeleteRun<T> {
  /**
   * The domain half: call the service, refresh the list, report success. Its
   * rejection is what puts the row into the error state, so let it throw.
   */
  work: (target: T) => Promise<void>;
  /** Words the failure for the error strip. */
  errorText: (error: unknown, target: T) => string;
}

export interface RowDeleteReturn<T> {
  confirmTarget: ShallowRef<T | null>;
  deletingUid: Ref<string | null>;
  rowError: Ref<RowDeleteError | null>;
  stateOf: (uid: string) => RowDeleteState;
  isActive: (uid: string) => boolean;
  errorTextOf: (uid: string) => string;
  arm: (target: T) => void;
  cancel: () => void;
  dismissError: () => void;
  run: (target: T, options: RowDeleteRun<T>) => Promise<void>;
}

/**
 * The confirm → busy → gone (or → error) state machine behind a list row's
 * delete, as `GaiaTableRowState` draws it.
 *
 * The redesign confirms a row delete by replacing the row with a strip rather
 * than by a popconfirm over it, which means the state lives with whoever owns
 * the row — not with `GaiaDeleteButton`, which the strip replaces. Seven views
 * had grown their own copy of these three refs and the ordering between them;
 * getting that ordering wrong (clearing the confirm after setting busy, or
 * missing the `finally`) leaves a row stuck mid-delete, so it belongs in one
 * place.
 *
 * `T` is whatever the view needs to word the strip — often just `{ uid }`, but
 * a view listing two kinds of row can carry the discriminator along:
 *
 * ```ts
 * const rowDelete = useRowDelete<{ uid: string; kind: 'contract' | 'file'; name: string }>();
 * ```
 */
export function useRowDelete<T extends RowDeleteTarget = RowDeleteTarget>(): RowDeleteReturn<T> {
  // The armed row travels whole so the strip can name what it is about to
  // delete; it is replaced rather than mutated, hence shallow.
  const confirmTarget: ShallowRef<T | null> = shallowRef(null);
  const deletingUid = ref<string | null>(null);
  const rowError = ref<RowDeleteError | null>(null);

  function stateOf(uid: string): RowDeleteState {
    if (deletingUid.value === uid) return 'busy';
    if (confirmTarget.value?.uid === uid) return 'confirm';
    if (rowError.value?.uid === uid) return 'error';
    return null;
  }

  function isActive(uid: string): boolean {
    return stateOf(uid) !== null;
  }

  /**
   * The row's failure text. A function rather than the ref itself because a
   * template only unwraps top-level bindings — `rowDelete.rowError.text` would
   * need a `.value` at every call site.
   */
  function errorTextOf(uid: string): string {
    return rowError.value?.uid === uid ? rowError.value.text : '';
  }

  /** Open the confirm strip, clearing any error the row was showing. */
  function arm(target: T): void {
    rowError.value = null;
    confirmTarget.value = target;
  }

  function cancel(): void {
    confirmTarget.value = null;
  }

  function dismissError(): void {
    rowError.value = null;
  }

  /**
   * Drive one delete. Takes the target explicitly rather than reading
   * `confirmTarget`, so the error strip's "Try again" is the same call as the
   * confirm strip's "Delete" — by then the confirm has been cleared.
   */
  async function run(target: T, { work, errorText }: RowDeleteRun<T>): Promise<void> {
    confirmTarget.value = null;
    rowError.value = null;
    deletingUid.value = target.uid;
    try {
      await work(target);
    } catch (error) {
      logger.error('Row delete failed:', error);
      rowError.value = { uid: target.uid, text: errorText(error, target) };
    } finally {
      deletingUid.value = null;
    }
  }

  return {
    confirmTarget,
    deletingUid,
    rowError,
    stateOf,
    isActive,
    errorTextOf,
    arm,
    cancel,
    dismissError,
    run,
  };
}
